/**
 * The code seam — see `./prose.ts` for why rendering is a seam at all.
 *
 * A `CodeRenderer` paints a block of code or terminal output into an element
 * the caller owns and keeps the frame, the metrics and any fallback of its
 * own inside that element, so a caller can re-render into the same element
 * on every delta without knowing what the renderer put there. `PlainCode` is
 * the default: a `<pre><code>`, read-only by nature, no dependencies.
 * `@tento-chat/adapters/tento-ui` supplies the CodeMirror-backed one.
 */
export interface CodeRenderer {
  render($into: HTMLElement, code: CodeRenderer.Code): void | Promise<void>;
}

export namespace CodeRenderer {
  export type Code = { text: string; language?: string };
}

export class PlainCode implements CodeRenderer {
  render($into: HTMLElement, code: CodeRenderer.Code): void {
    let $pre = $into.querySelector<HTMLPreElement>("pre[data-code='plain']");
    if (!$pre) {
      $pre = document.createElement("pre");
      $pre.dataset.code = "plain";
      $pre.style.margin = "0";
      $pre.style.whiteSpace = "pre";
      $pre.style.overflowX = "auto";
      $pre.style.fontFamily = "var(--chat-terminal-font, ui-monospace, SFMono-Regular, Menlo, monospace)";
      $pre.style.fontSize = "var(--chat-code-size, var(--chat-terminal-size, 12px))";
      $pre.style.lineHeight = "1.5";
      $pre.style.padding = "var(--chat-code-inset, 6px 8px)";
      $pre.appendChild(document.createElement("code"));
      $into.replaceChildren($pre);
    }
    const $code = $pre.firstElementChild as HTMLElement;
    if (code.language) $code.dataset.language = code.language;
    else delete $code.dataset.language;
    $code.textContent = code.text;
  }
}
